import { useState, useMemo, useEffect, useRef, useCallback } from "react";
import { useParams, useHistory } from "react-router-dom";
import Sidebar from "../../../sidebar/sidebar";
import Header from "../../../header/header";
import { injectKpiStyles } from "./kpiStyles";
import { useKpiData } from "../hooks/useKpiData";
import KpiPageHeader from "./KpiPageHeader";
import KpiFiltersBar from "./Kpifiltersbar";
import KpiFiltersModal from "./Kpifiltersmodal";
import KpiRow from "./KpiRow";
import BulkActionBar from "./BulkActionBar";
import AddDataModal from "./AddDataModal";
import ChatModal from "../../components/chatComponent/ChatModal";
import AuditTrailModal from "./AuditTrailModal";
import AssignModal from "./AssignModal";
import { apiCall } from "../../../../_services/apiCall";
import config from "../../../../config/config.json";

import BulkAssignModal from "./bulkAction/Bulkassignmodal";
import BulkReassignModal from "./bulkAction/Bulkreassignmodal";
import BulkPrefillModal from "./bulkAction/Bulkprefillmodal";
import BulkReminderModal from "./bulkAction/Bulkremindermodal";
import BulkAuditAssignModal from "./bulkAction/BulkAuditAssignModal";
import BulkRemoveDataOwnerModal from "./bulkAction/BulkRemoveDataOwner";
import ExcelUploader from "./ExcelUploader/ExcelUploader";
import { useCurrentUser } from "../../../../hooks/useCurrentUser";
import { PrimaryButton, SecondaryButton } from "../../../../globalComponents/ui/Button";

// Import cascade filter matcher from KpiTagFilter
import { kpiMatchesCascadeSteps } from "./KpiTagFilter";

const SESSION_KEY = "quantative_list_state_v2";

const loadSavedState = () => {
    try {
        return JSON.parse(sessionStorage.getItem(SESSION_KEY) || "null");
    } catch {
        return null;
    }
};

/* ─────────────────────────────────────────────────────────────
   Component
───────────────────────────────────────────────────────────── */
const QuantativeList = ({
    pageTitle = "Quantitative KPI",
    emissionMode = false,
    scopeFilter = null,
    summarySlot = null,
    ...props
}) => {
    // Don't restore saved state on scope pages — each scope starts clean
    const savedState = scopeFilter ? null : loadSavedState();
    const { moduleName } = useParams();
    const historyRouter = useHistory();



    useEffect(() => { injectKpiStyles(); }, []);

    const [sidebarExpanded, setSidebarExpanded] = useState(true);

    const [permissionList, setPermissionList] = useState([]);
    const [managementListValue, setManagementListValue] = useState([]);
    const [auditorType, setAuditorType] = useState([]);
    const isMounted = useRef(true);
    useEffect(() => () => { isMounted.current = false; }, []);

    // Check if a user has a specific permission
    const hasPermission = useCallback((permissionCode) => {
        return permissionList.some(
            (permission) =>
                permission.permissionCode === permissionCode && permission.checked
        );
    }, [permissionList]);

    useEffect(() => {
        const menu = JSON.parse(localStorage.getItem("menu") || "[]");
        const permissionsArray = [];
        menu.forEach((item) => {
            if (item.permissions) permissionsArray.push(...item.permissions);
            if (item.sub_menu) {
                item.sub_menu.forEach((sub) => {
                    if (sub.permissions) permissionsArray.push(...sub.permissions);
                });
            }
        });
        setPermissionList(permissionsArray);
    }, []);

    const userId = JSON.parse(localStorage.getItem("user_temp_id"));
    const { companyId, currentUserId } = useCurrentUser();
    const isExcelUploadEnabled = ["331", "351"].includes(String(companyId));
    const [dueDateOverrides, setDueDateOverrides] = useState({});
    const [reportingQuestionsMap, setReportingQuestionsMap] = useState({});

    const fetchFrameworkApi = useCallback(async () => {
        try {
            const { isSuccess, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}getFramework`,
                {},
                { type: "ALL" }
            );
            if (isSuccess) {
                return (data?.data || []).map((item) => item.id);
            }
        } catch (error) {
            console.error("Error fetching framework:", error);
        }
        return [];
    }, []);

    const fetchReportingQuestionsMap = useCallback(async (financialYearId) => {
        if (!financialYearId || !isExcelUploadEnabled) {
            setReportingQuestionsMap({});
            return;
        }

        try {
            const frameworkIds = await fetchFrameworkApi();
            const response = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}getReportingQuestionnew`,
                {},
                {
                    financialYearId,
                    frameworkIds,
                },
                "GET"
            );

            if (response?.isSuccess) {
                const nextMap = (response.data?.data || []).reduce((acc, item) => {
                    acc[item.questionId] = item;
                    return acc;
                }, {});
                setReportingQuestionsMap(nextMap);
            } else {
                setReportingQuestionsMap({});
            }
        } catch (error) {
            console.error("Error fetching reporting questions:", error);
            setReportingQuestionsMap({});
        }
    }, [fetchFrameworkApi, isExcelUploadEnabled]);

    const fetchDueDateOverrides = useCallback(async (financialYearId) => {
        if (!financialYearId || !isExcelUploadEnabled) {
            setDueDateOverrides({});
            return;
        }

        try {
            const response = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}dueDate/Overrides`,
                {},
                { financialYearId },
                "GET"
            );

            if (!response?.isSuccess) {
                setDueDateOverrides({});
                return;
            }

            const now = new Date();
            const grouped = (response.data?.data || []).reduce((acc, item) => {
                const overrideKey = `${item.financialYearId}-${item.sourceId}-${item.fromDate}-${item.toDate}-${item.questionId}`;
                if (!acc[overrideKey]) acc[overrideKey] = [];
                acc[overrideKey].push(item);
                return acc;
            }, {});

            const effectiveOverrides = Object.entries(grouped).reduce((acc, [key, list]) => {
                const approved = list.filter((item) => item.status === "approved");
                const pending = list.filter((item) => item.status === "pending");

                const validApproved = approved
                    .filter((item) => {
                        if (!item.dueDateTime) return false;
                        const dueDate = new Date(item.dueDateTime);
                        return !Number.isNaN(dueDate.getTime()) && dueDate > now;
                    })
                    .sort((a, b) => new Date(b.dueDateTime) - new Date(a.dueDateTime))[0];

                if (validApproved) {
                    acc[key] = validApproved;
                } else {
                    const latestPending = pending
                        .sort((a, b) => new Date(b.updatedAt || b.requestDate) - new Date(a.updatedAt || a.requestDate))[0];
                    if (latestPending) acc[key] = latestPending;
                }

                return acc;
            }, {});

            setDueDateOverrides(effectiveOverrides);
        } catch (error) {
            console.error("Error fetching due date overrides:", error);
            setDueDateOverrides({});
        }
    }, [isExcelUploadEnabled]);

    const getDesignation = async () => {
        try {
            const { isSuccess, data } = await apiCall(
                `${config.POSTLOGIN_API_URL_COMPANY}getMasterData`,
                {},
                { userId },
                "GET"
            );
            if (isSuccess && isMounted.current) {
                const nonAuditorRoles = data?.data.filter(
                    (role) =>
                        6 == role.id
                );
                const auditorRoles = data?.data.filter(
                    (role) => role.onlyauditor === true
                );
                setManagementListValue(nonAuditorRoles?.reverse());
                setAuditorType(auditorRoles?.reverse());
            }
        } catch (error) {
            console.error("Error fetching designation:", error);
        }
    };

    useEffect(() => {
        getDesignation();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    /* ──────────────────────────────────────────────────
       FILTERS
    ────────────────────────────────────────────────── */
    const [search, setSearch] = useState(savedState?.search || "");
    const [moduleFilter, setModuleFilter] = useState(savedState?.moduleFilter || moduleName || "");
    const [frameworkFilter, setFrameworkFilter] = useState(savedState?.frameworkFilter || "");
    const [fyFilter, setFyFilter] = useState(savedState?.fyFilter || "");
    const [expandedId, setExpandedId] = useState(savedState?.expandedId ?? null);

    // ── Cascade tag filter — restore Sets from serialized arrays ──
    const [filterSteps, setFilterSteps] = useState(() => {
        const saved = savedState?.filterSteps || [];
        return saved.map((s) => ({ key: s.key, values: new Set(s.values || []) }));
    });

    const [advFilters, setAdvFilters] = useState(() => {
        const saved = savedState?.advFilters || {};

        return {
            location_ids: Array.isArray(saved.location_ids) ? saved.location_ids : [],
            status: saved.status || "",
            from_date: saved.from_date || "",
            to_date: saved.to_date || "",
        };
    });

    const advFilterChange = (key, val) => setAdvFilters((p) => ({ ...p, [key]: val }));
    const advFilterCount = Object.values(advFilters).filter((v) =>
        Array.isArray(v) ? v.length > 0 : Boolean(v)
    ).length;

    const answerFilters = useMemo(() => ({
        location_ids: advFilters.location_ids,
        from_date: advFilters.from_date,
        to_date: advFilters.to_date,
    }), [advFilters.location_ids, advFilters.from_date, advFilters.to_date]);

    /* ─────────────── hook ─────────────── */
    const {
        kpiList,
        isLoading,
        error,
        financialYears,
        locations,
        modules,
        frameworks,
        selectedFY,
        setSelectedFY,
        saveAnswer,
        refetch,
        answeredQids
    } = useKpiData({ answerFilters, emissionMode });

    useEffect(() => {
        if (!selectedFY?.id) return;
        fetchReportingQuestionsMap(selectedFY.id);
        fetchDueDateOverrides(selectedFY.id);
    }, [selectedFY?.id, fetchReportingQuestionsMap, fetchDueDateOverrides]);

    useEffect(() => {
        if (moduleName) setModuleFilter(moduleName);
    }, [moduleName]);

    useEffect(() => {
        if (selectedFY?.id && !fyFilter) {
            setFyFilter(String(selectedFY.id));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [selectedFY?.id]);

    // ── Persist state to sessionStorage — serialize Sets to arrays ──
    useEffect(() => {
        const state = {
            search,
            moduleFilter,
            fyFilter,
            frameworkFilter,
            advFilters,
            expandedId,
            filterSteps: filterSteps.map((s) => ({ key: s.key, values: [...(s.values || [])] })),
        };
        const existing = loadSavedState() || {};
        const { scrollTop: _drop, ...rest } = existing;
        sessionStorage.setItem(SESSION_KEY, JSON.stringify({ ...rest, ...state }));
    }, [search, moduleFilter, fyFilter, frameworkFilter, advFilters, expandedId, filterSteps]);

    const scrollContainerRef = useRef(null);
    const scrollRestored = useRef(false);

    useEffect(() => {
        if (scrollRestored.current) return;
        if (!expandedId) return;
        if (!kpiList.length) return;

        const doScroll = () => {
            const el = document.querySelector(`[data-kpi-id="${expandedId}"]`);
            if (!el) return false;
            el.scrollIntoView({ block: "center", behavior: "instant" });
            scrollRestored.current = true;
            return true;
        };

        let raf1, raf2, t;
        raf1 = requestAnimationFrame(() => {
            if (!doScroll()) {
                raf2 = requestAnimationFrame(() => {
                    if (!doScroll()) {
                        t = setTimeout(doScroll, 200);
                    }
                });
            }
        });

        return () => {
            cancelAnimationFrame(raf1);
            cancelAnimationFrame(raf2);
            clearTimeout(t);
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [kpiList.length]);

    const handleScroll = useCallback(() => { }, []);


    const scopeFilteredList = useMemo(() => {
        if (!scopeFilter) return kpiList;
        const normalised = scopeFilter.toLowerCase().replace(/\s+/g, "");


        return kpiList.filter((item) => {
            const combos = item.rawTags;
            if (!Array.isArray(combos) || combos.length === 0) return false;


            return combos.some((combo) =>
                Array.isArray(combo) &&
                combo.some(
                    (pair) =>
                        pair?.key?.toLowerCase() === "scope" &&
                        pair?.value?.toLowerCase().replace(/\s+/g, "") === normalised
                )
            );
        });
    }, [kpiList, scopeFilter]);

    const lockedSteps = useMemo(() => {
        if (!scopeFilter) return [];
        // "SCOPE1" → "scope 1", "SCOPE2" → "scope 2", "SCOPE3" → "scope 3"
        const normalised = scopeFilter.toLowerCase().replace(/\s+/g, "").replace(/scope(\d)/, "scope $1");

        return [{ key: "scope", values: new Set([normalised]) }];
    }, [scopeFilter]);

    /* ─────────────── Filtered list using cascade matcher ─────────────── */
    const getKpiStatus = (item) => {
        const isAdmin = Number(currentUserId) === 1;
        const rawHistory = item.quantative_history || [];
        const history = isAdmin
            ? rawHistory
            : rawHistory.filter(ans => Number(ans.createdById) === Number(currentUserId));

        const total = history.length;
        if (total > 0) {
            const accepted = history.filter(ans => ans.status?.toLowerCase() === 'approved').length;
            if (accepted === total) return "completed";
        }
        return "pending";
    };

    const filtered = useMemo(() => {
        return scopeFilteredList.filter((item) => {
            if (emissionMode && !answeredQids.has(String(item.id))) return false;
            if (search && !item.kpi_name.toLowerCase().includes(search.toLowerCase())) return false;
            if (moduleFilter && item.module !== moduleFilter) return false;
            if (
                frameworkFilter &&
                (!Array.isArray(item.mapFrameworkIds) ||
                    !item.mapFrameworkIds.includes(String(frameworkFilter)))
            ) return false;

            const itemStatus = getKpiStatus(item);
            if (advFilters.status === "pending" && itemStatus !== "pending") return false;
            if (advFilters.status === "completed" && itemStatus !== "completed") return false;

            // ── Cascade tag filter ──
            if (!kpiMatchesCascadeSteps(item, filterSteps)) return false;
            return true;
        });
    }, [kpiList, search, moduleFilter, frameworkFilter, advFilters.status, filterSteps, emissionMode, answeredQids, scopeFilteredList]);

    const completedCount = filtered.filter((i) => getKpiStatus(i) === "completed").length;
    const pendingCount = filtered.length - completedCount;

    const selectedLocations = locations.filter((l) =>
        Array.isArray(advFilters.location_ids) &&
        advFilters.location_ids.includes(l.id)
    );


    const [selectedIds, setSelectedIds] = useState(new Set());
    const allSelected = filtered.length > 0 && filtered.every((i) => selectedIds.has(i.id));

    const toggleOne = (id) =>
        setSelectedIds((prev) => {
            const next = new Set(prev);
            next.has(id) ? next.delete(id) : next.add(id);
            return next;
        });
    const toggleAll = () =>
        allSelected
            ? setSelectedIds(new Set())
            : setSelectedIds(new Set(filtered.map((i) => i.id)));
    const clearSelection = () => setSelectedIds(new Set());

    const selectedIdsArray = useMemo(() => [...selectedIds], [selectedIds]);

    /* ── Per-row modal state ── */
    const [showFiltersModal, setShowFiltersModal] = useState(false);
    const [modal, setModal] = useState({ type: null, item: null });
    const openModal = (type, item) => setModal({ type, item });
    const closeModal = () => setModal({ type: null, item: null });

    const [bulkModal, setBulkModal] = useState(null);
    const closeBulkModal = useCallback(() => setBulkModal(null), []);

    const handleBulkAction = useCallback((actionKey) => {
        setBulkModal(actionKey);
    }, []);

    /* ── Helpers for active filter step count ── */
    const activeFilterStepCount = filterSteps.filter((s) => s.values?.size > 0).length;
    const hasAnyFilter =
        search ||
        moduleFilter ||
        fyFilter ||
        frameworkFilter ||
        advFilterCount > 0 ||
        activeFilterStepCount > 0;

    /* ── Clear ALL filters including cascade tag steps ── */
    const clearAllFilters = () => {
        setSearch("");
        setModuleFilter("");
        setFyFilter("");
        setFrameworkFilter("");
        setAdvFilters({ location_ids: [], status: "", from_date: "", to_date: "" });
        setFilterSteps([]);
        sessionStorage.removeItem(SESSION_KEY);
    };

    /* ── Handle FY change ── */
    const handleFyChange = (v) => {
        setFyFilter(v);
        if (v) {
            const fy = financialYears.find((f) => String(f.id) === String(v));
            if (fy) setSelectedFY({ id: fy.id, name: fy.financial_year_value || fy.name, financial_year_value: fy.financial_year_value });
        }
    };

    const handleOpenHistory = useCallback((item) => {
        const statePayload = {
            kpiName: item.kpi_name,
            financialYearId: selectedFY?.id,
            financialYearName: selectedFY?.name,
            locations: locations,
            fromDate: advFilters.from_date ?? "",
            toDate: advFilters.to_date ?? "",
            module: item.module,
            assignedTo: item.assigned_to,
            unitSetting: item.unitSetting ?? null,
            emissionMode: emissionMode
        };
        sessionStorage.setItem(`ahp_state_${item.id}`, JSON.stringify(statePayload));
        historyRouter.push(`/data-managment/quantative/${item.id}`, statePayload);
    }, [historyRouter, selectedFY, locations, advFilters]);

    /* ─────────────── Column header helper ─────────────── */
    const ColLabel = ({ children, width, flex, align = "left" }) => (
        <div style={{
            ...(width ? { width, flexShrink: 0 } : {}),
            ...(flex ? { flex } : {}),
            padding: "0 10px",
            fontSize: 10,
            fontWeight: 700,
            color: "var(--ink-4)",
            textTransform: "uppercase",
            letterSpacing: "0.07em",
            fontFamily: "var(--font-mono)",
            textAlign: align,
        }}>
            {children}
        </div>
    );

    /* ─────────────────────────────────────────────────────────
       RENDER
    ───────────────────────────────────────────────────────── */
    if (!hasPermission("LIST_ALL_KPIS") && !hasPermission("LIST_ASSIGNED_KPIS") && permissionList.length > 0) {
        return (
            <div style={{ display: "flex", background: "var(--bg)", minHeight: "100vh", alignItems: "center", justifyContent: "center" }}>
                <div style={{ textAlign: "center" }}>
                    <h1 style={{ fontSize: 24, fontWeight: 700, color: "var(--ink)" }}>Access Denied</h1>
                    <p style={{ color: "var(--ink-4)" }}>You do not have permission to view this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div
            className="kpi-root"
            style={{
                display: "flex",
                background: "var(--surface-2)",
                minHeight: "100vh",
                fontFamily: "var(--font-sans)",
            }}
        >
            <div
                style={{
                    flex: sidebarExpanded ? "0 0 21%" : "0 0 60px",
                    position: "sticky",
                    top: 0,
                    height: "100vh",
                    background: "var(--surface)",
                    borderRight: "1px solid var(--rule)",
                    zIndex: 100,
                    transition: "flex 0.2s ease",
                }}
            >
                <Sidebar
                    dataFromParent={props.location?.pathname || "/dashboard"}
                    onSidebarToggle={(isOpen) => setSidebarExpanded(isOpen)}
                />
            </div>

            <main style={{ flex: 1, display: "flex", flexDirection: "column", overflow: "hidden", minWidth: 0 }}>
                <Header />



                <KpiPageHeader
                    pageTitle={pageTitle}
                    selectedFY={selectedFY}
                    totalCount={filtered.length}
                    completedCount={completedCount}
                    pendingCount={pendingCount}
                    selectedLocation={selectedLocations}
                    onClearLocation={() => advFilterChange("location_id", "")}
                    onOpenFilters={() => setShowFiltersModal(true)}
                    advFilterCount={advFilterCount}
                    rightSlot={
                        hasPermission("UPLOAD_EXCEL") && (
                            isExcelUploadEnabled ? (
                                <ExcelUploader
                                    financialYearId={selectedFY?.id}
                                    financialYear={financialYears}
                                    dueDateOverrides={dueDateOverrides}
                                    reportingQuestionsMap={reportingQuestionsMap}
                                />
                            ) : (
                                <PrimaryButton type="button">
                                    Upload
                                </PrimaryButton>
                            )
                        )
                    }
                    summarySlot
                />


                <KpiFiltersBar
                    search={search}
                    onSearchChange={setSearch}
                    moduleFilter={moduleFilter}
                    onModuleChange={setModuleFilter}
                    fyFilter={fyFilter}
                    onFyChange={handleFyChange}
                    frameworkFilter={frameworkFilter}
                    onFrameworkChange={setFrameworkFilter}
                    financialYears={financialYears}
                    modules={modules}
                    frameworks={frameworks}
                    locations={locations}
                    advFilterCount={advFilterCount}
                    onOpenAdvanced={() => setShowFiltersModal(true)}
                    onClearAll={clearAllFilters}
                    // ── Cascade tag filter props ──────────────────────────
                    kpiList={kpiList}
                    filterSteps={filterSteps}
                    onFilterStepsChange={setFilterSteps}
                    lockedSteps={lockedSteps}
                />

                {summarySlot && (
                    <div style={{ padding: "12px 24px 4px" }}>
                        {summarySlot}
                    </div>
                )}
                <BulkActionBar
                    count={selectedIds.size}
                    selectedIds={selectedIdsArray}
                    onAction={handleBulkAction}
                    onDeselect={clearSelection}
                    hasPermission={hasPermission}
                />

                <div
                    ref={scrollContainerRef}
                    onScroll={handleScroll}
                    style={{ flex: 1, overflow: "auto", padding: "16px 24px 28px" }}
                >
                    {/* ── Active filter indicator chips ── */}
                    <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, paddingLeft: 4 }}>
                        {selectedIds.size > 0 && (
                            <>
                                <span style={{ color: "#d1d5db", fontSize: 14, lineHeight: 1 }}>·</span>
                                <span style={{ fontSize: 12, color: "var(--accent)", fontWeight: 600 }}>{selectedIds.size} selected</span>
                            </>
                        )}
                        {advFilters.location_ids?.length > 0 && (
                            <>
                                <span style={{ color: "#d1d5db", fontSize: 14, lineHeight: 1 }}>·</span>
                                <span style={{ fontSize: 11.5, color: "var(--accent)", fontWeight: 600 }}>
                                    📍 {selectedLocations.length} location{selectedLocations.length > 1 ? "s" : ""} selected
                                </span>
                            </>
                        )}
                        {(advFilters.from_date || advFilters.to_date) && (
                            <>
                                <span style={{ color: "#d1d5db", fontSize: 14, lineHeight: 1 }}>·</span>
                                <span style={{ fontSize: 11.5, color: "var(--accent)", fontWeight: 600 }}>
                                    📅 {advFilters.from_date || "…"} → {advFilters.to_date || "…"}
                                </span>
                            </>
                        )}
                        {/* ── Active cascade tag filter chip ── */}
                        {activeFilterStepCount > 0 && (
                            <>
                                <span style={{ color: "#d1d5db", fontSize: 14, lineHeight: 1 }}>·</span>
                                <span style={{ fontSize: 11.5, color: "#4338ca", fontWeight: 600 }}>
                                    🏷️ {activeFilterStepCount} tag filter{activeFilterStepCount > 1 ? "s" : ""} active
                                </span>
                            </>
                        )}
                    </div>

                    <div style={{
                        background: "var(--surface)",
                        borderRadius: 12,
                        border: "1px solid var(--rule)",
                        overflow: "hidden",
                        boxShadow: "0 1px 3px rgba(0,0,0,0.04), 0 4px 16px rgba(0,0,0,0.05)",
                    }}>
                        {/* ── Column headers ── */}
                        <div style={{
                            display: "flex",
                            alignItems: "center",
                            padding: "10px 0",
                            background: "var(--surface-3)",
                            borderBottom: "2px solid #e5e8ed",
                            position: "sticky",
                            top: 0,
                            zIndex: 10,
                        }}>
                            <div style={{ width: 46, flexShrink: 0, display: "flex", justifyContent: "center" }}>
                                <input type="checkbox" className="kpi-checkbox" checked={allSelected} onChange={toggleAll} title={allSelected ? "Deselect all" : "Select all"} disabled={emissionMode} />
                            </div>
                            <div style={{ width: 32, flexShrink: 0 }} />
                            <ColLabel flex={1}>KPI</ColLabel>
                            <ColLabel width={150}>Assigned To</ColLabel>
                            <ColLabel width={130}>Assigned By</ColLabel>
                            <ColLabel width={120}>Status</ColLabel>
                            <ColLabel width={120}> {emissionMode ? "Emission (FY)" : "Value"}</ColLabel>
                            <div style={{ width: 44, flexShrink: 0 }} />
                        </div>

                        {/* ── Loading skeleton ── */}
                        {isLoading && (
                            <div>
                                {[1, 2, 3, 4, 5, 6].map((i) => (
                                    <div key={i} style={{ display: "flex", alignItems: "center", padding: "18px 20px", borderBottom: "1px solid var(--rule-2)", gap: 14, opacity: 1 - i * 0.1 }}>
                                        <div style={{ width: 16, height: 16, borderRadius: 4, background: "#eaecf0", flexShrink: 0 }} />
                                        <div style={{ width: 20, height: 12, borderRadius: 4, background: "#eaecf0", flexShrink: 0 }} />
                                        <div style={{ flex: 1, display: "flex", flexDirection: "column", gap: 7 }}>
                                            <div className="kpi-shimmer" style={{ height: 13, borderRadius: 4, width: `${60 + (i * 11) % 35}%`, background: "#eaecf0" }} />
                                            <div style={{ height: 10, borderRadius: 4, background: "#f1f3f6", width: "28%" }} />
                                        </div>
                                        {[130, 110, 100, 88].map((w) => (
                                            <div key={w} style={{ width: w, height: 26, borderRadius: 6, background: "#eaecf0", flexShrink: 0 }} />
                                        ))}
                                        <div style={{ width: 68, height: 22, borderRadius: 20, background: "#eaecf0", flexShrink: 0 }} />
                                        <div style={{ width: 24, height: 24, borderRadius: "50%", background: "#eaecf0", flexShrink: 0 }} />
                                    </div>
                                ))}
                                <div style={{ display: "flex", alignItems: "center", justifyContent: "center", padding: "14px 24px", gap: 10, color: "var(--ink-4)", fontSize: 12, fontFamily: "var(--font-sans)", borderTop: "1px solid var(--rule-2)" }}>
                                    <div className="kpi-spinner" style={{ width: 16, height: 16, borderWidth: 2 }} />
                                    Loading KPIs…
                                </div>
                            </div>
                        )}

                        {/* ── Error state ── */}
                        {!isLoading && error && (
                            <div style={{ padding: "52px 24px", textAlign: "center", fontSize: 13 }}>
                                <div style={{ fontSize: 34, marginBottom: 12 }}>⚠️</div>
                                <div style={{ fontWeight: 700, marginBottom: 6, fontSize: 14, color: "var(--red)" }}>Failed to load data</div>
                                <div style={{ color: "var(--ink-4)", fontSize: 13, marginBottom: 20 }}>{error}</div>
                                <SecondaryButton onClick={() => refetch()}>↻ Retry</SecondaryButton>
                            </div>
                        )}

                        {/* ── Empty state ── */}
                        {!isLoading && !error && filtered.length === 0 && (
                            <div style={{ display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", padding: "68px 24px", gap: 10 }}>
                                <span style={{ fontSize: 40, opacity: 0.35 }}>📊</span>
                                <div style={{ fontWeight: 700, fontSize: 14, color: "var(--ink-2)", fontFamily: "var(--font-sans)" }}>No Data Found</div>
                                <div style={{ fontSize: 12.5, color: "var(--ink-4)" }}>
                                    {(advFilters.from_date || advFilters.to_date)
                                        ? "No data found for that date range."
                                        : activeFilterStepCount > 0
                                            ? "No KPIs match the selected tags. Try selecting different tags or clear the tag filter."
                                            : "Try adjusting the filters or search term."}
                                </div>
                                {hasAnyFilter && (
                                    <PrimaryButton className="kpi-btn-secondary" onClick={clearAllFilters} style={{ marginTop: 8 }}>Clear all filters</PrimaryButton>
                                )}
                            </div>
                        )}

                        {/* ── KPI rows ── */}
                        {!isLoading && !error && filtered.map((item, index) => (
                            <KpiRow
                                key={item.id}
                                item={item}
                                index={index}
                                selected={selectedIds.has(item.id)}
                                onToggle={() => toggleOne(item.id)}
                                searchQuery={search}
                                expanded={expandedId === item.id}
                                onExpand={() => setExpandedId(prev => prev === item.id ? null : item.id)}
                                onAdd={(i) => hasPermission("ADD_KPI_DATA_RECORD") && openModal("add", i)}
                                onHistory={handleOpenHistory}
                                onChat={(i) => openModal("chat", i)}
                                onAudit={(i) => openModal("audit", i)}
                                onAssign={(i) => hasPermission("ASSIGN_DATA_OWNER") && openModal("assign", i)}
                                isEven={index % 2 === 0}
                                emissionMode={emissionMode}
                                hasPermission={hasPermission}
                            />
                        ))}

                        {/* ── Footer summary ── */}
                        {!isLoading && !error && filtered.length > 0 && (
                            <div style={{
                                padding: "11px 20px",
                                borderTop: "1px solid var(--rule-2)",
                                background: "#f8f9fb",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                            }}>
                                <span style={{ fontSize: 11.5, color: "var(--ink-4)", fontFamily: "var(--font-mono)" }}>
                                    Showing {filtered.length} of {kpiList.length} KPIs
                                    {activeFilterStepCount > 0 && (
                                        <span style={{ marginLeft: 6, color: "#4338ca" }}>
                                            · {activeFilterStepCount} tag filter{activeFilterStepCount > 1 ? "s" : ""} active
                                        </span>
                                    )}
                                </span>
                                <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--green)", fontWeight: 600 }}>
                                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--green)", display: "inline-block" }} />
                                        {completedCount} done
                                    </span>
                                    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, fontSize: 11.5, color: "var(--amber)", fontWeight: 600 }}>
                                        <span style={{ width: 6, height: 6, borderRadius: "50%", background: "var(--amber)", display: "inline-block" }} />
                                        {pendingCount} pending
                                    </span>
                                </div>
                            </div>
                        )}
                    </div>
                </div>
            </main>

            {/* ── Modals ── */}
            <KpiFiltersModal
                show={showFiltersModal}
                onHide={() => setShowFiltersModal(false)}
                filters={advFilters}
                onChange={advFilterChange}
                onApply={() => { }}
                onReset={() => setAdvFilters({ location_ids: [], status: "", from_date: "", to_date: "" })}
                locations={locations}
                filteredCount={filtered.length}
                isLoading={isLoading}
            />

            <AddDataModal
                show={modal.type === "add"}
                onHide={closeModal}
                item={modal.item}
                financialYearId={selectedFY?.id}
                locations={locations}
                saveAnswer={saveAnswer}
                onSave={(item, data) => {
                    console.log("[AddData] saved", item.id, data);
                    refetch();
                }}
            />

            <ChatModal
                show={modal.type === "chat"}
                onHide={closeModal}
                item={modal.item}
                questionId={modal.item?.id}
                financialYearId={modal.item?.financial_year_id}
                sourceId={modal.item?.location_id}
                fromDate={modal.item?.from_date}
                toDate={modal.item?.to_date}
                currentUserId={JSON.parse(localStorage.getItem("currentUser") || "null")?.id ?? null}
                userForRole={JSON.parse(localStorage.getItem("currentUser") || "null") ?? {}}
                participants={[]}
                initialMessages={[]}
                onSend={(msg, recipients) => console.log("[Chat] send", msg, recipients)}
            />

            <AuditTrailModal
                show={modal.type === "audit"}
                onHide={closeModal}
                item={modal.item}
                financialYearId={selectedFY?.id}
            />

            <AssignModal
                show={modal.type === "assign"}
                onHide={closeModal}
                item={modal.item}
                assignDetailsFromApi={modal.item?.assign_raw || null}
                sources={locations}
            />

            <BulkAssignModal
                show={bulkModal === "assign"}
                onHide={closeBulkModal}
                selectedIds={selectedIdsArray}
                financialYearId={selectedFY?.id}
                managementListValue={managementListValue}
                onSuccess={() => { clearSelection(); refetch(); }}
            />

            <BulkReassignModal
                show={bulkModal === "reassign"}
                onHide={closeBulkModal}
                selectedIds={selectedIdsArray}
                financialYearId={selectedFY?.id}
                managementListValue={managementListValue}
                onSuccess={() => { clearSelection(); refetch(); }}
            />

            <BulkPrefillModal
                show={bulkModal === "prefill"}
                onHide={closeBulkModal}
                selectedIds={selectedIdsArray}
                financialYears={financialYears}
                selectedFinancialYearId={selectedFY?.id}
                onSuccess={() => { clearSelection(); refetch(); }}
            />

            <BulkReminderModal
                show={bulkModal === "reminder"}
                onHide={closeBulkModal}
                selectedIds={selectedIdsArray}
                onSuccess={() => clearSelection()}
            />

            <BulkAuditAssignModal
                show={bulkModal === "auditAssign"}
                onHide={closeBulkModal}
                selectedIds={selectedIdsArray}
                financialYearId={selectedFY?.id}
                locationId={advFilters.location_id || null}
                companyId={JSON.parse(localStorage.getItem("currentUser") || "null")?.companyId ?? null}
                managementListValue={auditorType}
                onSuccess={() => { clearSelection(); refetch(); }}
            />

            <BulkRemoveDataOwnerModal
                show={bulkModal === "removeDataOwner"}
                onHide={closeBulkModal}
                selectedKpis={kpiList.filter(kpi => selectedIds.has(kpi.id))}
                onSuccess={() => { clearSelection(); refetch(); }}
            />
        </div>
    );
};

export default QuantativeList;
